import { MEDIA_TYPES, MEDIA_SIZES } from 'aria-viewer/tour/constants'
import { FRONT } from 'aria-viewer/navigation/navigationActions'
import UriHandler from 'aria-viewer/utils/uriHandler'
import { WTPhotoController } from '../legacy'
import MobileDetect from 'aria-viewer/utils/mobileDetect'
import EasyFragmentShader from 'public/assets/shaders/easy.fs'
import EasyVertexShader from 'public/assets/shaders/easy.vs'
import { getHasEasy } from './util'
import { deviceDetails } from 'aria-viewer/device/deviceReducer'

const addTrailingSlash = (string = '') => `${string}${string.endsWith('/') ? '' : '/'}`

let mediaPaths = {}
export const setMediaPaths = models => {
    mediaPaths = models
}
export const getMediaPaths = () => mediaPaths

export const internalGetMainMedia = ({
    stop,
    point,
    orientation,
    canDeriveMediaFromStops,
    mainMedia,
    hotspots,
    enableAccelerometer,
}) => {
    // Retrieve main media for a stop. Check if point is at a stop and the stop has main media. if there is no point
    // then retrieve main media for the stop
    if (
        canDeriveMediaFromStops &&
        stop &&
        stop.main_media_id &&
        stop.main_media_type &&
        (point == null || point.stopid)
    ) {
        const media = stop[`${stop.main_media_type}s`].find(({ id }) => id === stop.main_media_id)
        if (media) {
            let newMedia = media
            if (hotspots) {
                let newHotspots = []
                const currentMainMediaHotspots = hotspots[media.id]
                for (let hotspotIndex in currentMainMediaHotspots) {
                    newHotspots.push(currentMainMediaHotspots[hotspotIndex])
                }
                newMedia = Object.assign({}, media, {
                    hotspots: newHotspots,
                })
            }
            return transformMedia({
                media: newMedia,
                alt: newMedia.description || (stop && stop.title),
                orientation,
                mainMedia,
                enableAccelerometer,
            })
        }
    }

    if (point == null) {
        return null
    }

    return transformMedia({
        media: point,
        alt: stop && stop.title,
        orientation,
        mainMedia,
        enableAccelerometer,
    })
}

const replicateUrl = url => {
    let values = {}
    Object.keys(MEDIA_SIZES).forEach(key => {
        values[key] = url
    })
    return values
}

const buildPointUrl = (path, size) => {
    //in production url has cloudfront in it... so we cahnged to /front
    return path.replace('/front', '/' + size + '_front.jpg')
}

//Format a generic path received
//path ex: /media/LOC_ID/myMediaType/ID/SIZE
//file ex: testImage.jpg
const buildRelativeUrl = ({ path, loc_id, mediaId, file }) => {
    return path
        .replace('LOC_ID', loc_id)
        .replace('ID', mediaId)
        .replace('SIZE', file)
}

//Build an object from an array of sizes
//ex: { tiny: "something.jpg" }
const buildSizesObject = ({ path, sizes = [], media }) => {
    return sizes.reduce((sizeObj, { label, size }) => {
        //For each size, add the label to the sizeObj with a clean url
        return Object.assign(sizeObj, {
            [`${label}`]: UriHandler.fixProtocol(
                buildRelativeUrl({
                    path,
                    loc_id: media.loc_id || media.locId,
                    mediaId: media.id,
                    file: `${size}.jpg`,
                })
            ),
        })
    }, {})
}

const urlWithCorsAndProtocolSupport = url => {
    if (!UriHandler.isInSameSubDomain(url, window.location.href)) {
        url = UriHandler.appendCorsQueryParam(url)
    }

    return UriHandler.fixProtocol(url)
}

//Transform the media fields for external use
export const transformMedia = ({
    media = {},
    orientation = FRONT,
    mainMedia,
    alt = 'media',
    enableAccelerometer = false,
}) => {
    const { mediaType } = media
    const { path, sizes = {} } = mediaPaths[mediaType] || {}
    const { grade: deviceGrade } = deviceDetails
    let mediaSizes

    switch (mediaType) {
        case MEDIA_TYPES.panorama:
            mediaSizes = buildSizesObject({ media, path, sizes: sizes.supplementalMedia })

            const shader = media.hasEasy
                ? {
                      vertex: EasyVertexShader,
                      fragment: EasyFragmentShader,
                  }
                : undefined

            return {
                ...media,
                id: media.id,
                alt: alt,
                path: media.path,
                showRotate: false,
                showFullscreen: false,
                startLon: null,
                startLat: null,
                startFov: media.start_fov && !MobileDetect.phone() ? media.start_fov : 100,
                hintTimeout: 5000,
                businessCard: '',
                shader,
                mediaType,
                description: media.description,
                stopid: media.stopid,
                urls: mediaSizes,
                hasEasy: getHasEasy(media),
                width: window.innerWidth,
                height: window.innerHeight,
                accelerometerControls: enableAccelerometer,
            }
        case MEDIA_TYPES.photo:
            let mediaDir = null
            if (!mainMedia) {
                mediaSizes = buildSizesObject({ media, path, sizes: sizes.supplementalMedia })
            } else {
                let url = buildRelativeUrl({
                    path,
                    loc_id: media.loc_id || media.locId,
                    mediaId: media.id,
                    file: '',
                })
                // Remove the last slash from the url before passing to PhotoController
                mediaDir = url.slice(0, -1)
                url = WTPhotoController.getPhoto(mediaDir)
                mediaSizes = replicateUrl(url)
            }

            return Object.assign({}, media, {
                size: MEDIA_SIZES.medium,
                id: media.id,
                alt: alt,
                mediaType,
                description: media.description,
                stopid: media.stopid,
                urls: mediaSizes,
                path: mediaDir,
            })
        case MEDIA_TYPES.video:
        case MEDIA_TYPES.video360:
            return {
                ...media,
                alt: alt,
                autoplay: false,
                hideUI: true,
                accessPlayerAPI: true,
                playback_type: media.playback_type,
                type: media.type,
                videoId: media.videoid,
                mediaType,
                id: media.id,
                description: media.description,
                stopid: media.stopid,
                instructionIndex: 1,
                urls: {
                    thumb: UriHandler.fixProtocol(media.tinyurl),
                    tiny: urlWithCorsAndProtocolSupport(media.tinyurl),
                    small: urlWithCorsAndProtocolSupport(media.smallurl),
                    medium: urlWithCorsAndProtocolSupport(media.mediumurl),
                    large: urlWithCorsAndProtocolSupport(media.largeurl),
                },
                accelerometerControls: enableAccelerometer,
                deviceGrade,
            }
        default:
            const direction = orientation.toLowerCase()
            const dir = media.dir && UriHandler.fixProtocol(addTrailingSlash(media.dir))
            const templatePath = `${dir}${direction}`
            return {
                mediaType: MEDIA_TYPES.photo,
                alt: alt,
                walkingTourPhoto: true,
                path: templatePath,
                urls: {
                    // TODO, the numbers for the URL sizes should be derived from mediaPaths[photo].mainMediaWT
                    thumb: buildPointUrl(templatePath, 560),
                    tiny: buildPointUrl(templatePath, 560),
                    small: buildPointUrl(templatePath, 560),
                    medium: buildPointUrl(templatePath, 1024),
                    large: WTPhotoController.getPhoto(templatePath),
                },
                size: MEDIA_SIZES.large,
                stopid: media.stopid,
            }
    }
}
